import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/api/supabaseClient';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import logoUrl from '@/assets/logo-horizontal.png';

// Module-scope shell (same pattern as CarrierAuth — defining inside the page
// would remount per render and steal focus on every keystroke).
// Brand-aligned light theme per the Pro Haul Brand Style Guide.
// Matches CarrierAuth so the entire public-auth experience is
// visually consistent (white bg, prohaul-black text, prohaul-red
// for primary actions).
const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-prohaul-white text-prohaul-black flex flex-col items-center justify-center px-4 py-10">
    <div className="w-full max-w-sm space-y-6 flex-1 flex flex-col justify-center">
      <div className="text-center">
        <img
          src={logoUrl}
          alt="Pro Haul"
          className="w-72 mx-auto mb-5 block"
        />
        <h1 className="text-2xl font-stencil font-bold text-prohaul-black tracking-wide">
          {title}
        </h1>
        {subtitle && <p className="text-prohaul-black/70 text-sm mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
    <footer className="text-xs text-prohaul-black/60 flex items-center gap-4 pt-8">
      <a href="/terms"   className="hover:text-prohaul-red">Terms</a>
      <span className="opacity-40">·</span>
      <a href="/privacy" className="hover:text-prohaul-red">Privacy</a>
    </footer>
  </div>
);

const ROLE_LABEL = {
  admin: 'an Admin',
  hr: 'on the HR team',
  accounting: 'on the Accounting team',
  dispatch: 'as a Dispatcher',
  driver: 'as a Driver',
};

// Returns the status-state screen for non-pending invitations (or null).
const renderStatusScreen = (status) => {
  const screens = {
    accepted: {
      title: 'Invitation already accepted',
      body: 'This invitation has already been used. Sign in to access the carrier workspace.',
    },
    revoked: {
      title: 'Invitation revoked',
      body: 'This invitation was revoked by the carrier. Ask them to send a new one if you should still have access.',
    },
    expired: {
      title: 'Invitation expired',
      body: 'This invitation is past its 7-day window. Ask the carrier admin to send a fresh invite.',
    },
  };
  return screens[status] || null;
};

// Persistent inline error banner. Unlike a toast it stays on screen until
// the user changes the form — critical on mobile, where Teresa kept
// missing the ~3s toast and thought the button did nothing.
const ErrorBanner = ({ message }) =>
  message ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-prohaul-red flex-shrink-0 mt-0.5" />
      <p className="text-xs text-prohaul-black">{message}</p>
    </div>
  ) : null;

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const { user, isAuthenticated, refreshUser, logout } = useAuth();

  const [previewLoading, setPreviewLoading] = useState(true);
  const [preview, setPreview]               = useState(null);   // { carrier_name, invited_role, invited_email, status, expires_at }
  const [previewError, setPreviewError]     = useState(null);

  // Unauthenticated invitee sub-form.
  //   'create' — brand-new user: pick a password, join in one screen.
  //   'signin' — invitee who already has a Pro Haul account.
  const [authMode, setAuthMode] = useState('create');
  const [password, setPassword] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [formError, setFormError] = useState(null);   // persistent inline error

  // accept action (for the already-authenticated path)
  const [accepting, setAccepting] = useState(false);

  // ---- fetch the preview on mount ----
  useEffect(() => {
    if (!token) { setPreviewError('Missing invitation token in the URL.'); setPreviewLoading(false); return; }
    (async () => {
      try {
        const rows = await client.rpc('get_invitation_preview', { p_token: token });
        if (!rows || rows.length === 0) {
          setPreviewError('We could not find that invitation. The link may be malformed or the invite was deleted.');
        } else {
          setPreview(rows[0]);
        }
      } catch (e) {
        setPreviewError(e?.message || 'Could not look up that invitation.');
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [token]);

  // Whether the signed-in user matches the invitation's intended recipient.
  const emailMatches = useMemo(() => {
    if (!isAuthenticated || !preview) return false;
    return (user?.email || '').toLowerCase() === preview.invited_email.toLowerCase();
  }, [isAuthenticated, user?.email, preview]);

  // ---- accept action (already-authenticated existing user) ----
  const onAccept = async () => {
    setAccepting(true);
    try {
      await client.rpc('accept_invitation', { p_token: token });
      await refreshUser();
      toast.success(`Welcome to ${preview.carrier_name}.`);
      navigate('/', { replace: true });
    } catch (e) {
      // Surface RPC error messages verbatim — they're already user-readable
      // (e.g. "you already belong to a carrier; leave it first to accept a new invite").
      toast.error(e?.message || 'Could not accept the invitation.');
    } finally {
      setAccepting(false);
    }
  };

  // ---- mode switch helper (clears stale errors + password) ----
  const switchMode = (mode) => {
    setAuthMode(mode);
    setFormError(null);
  };

  // ---- create-account path: one screen, no second confirmation email ----
  // POSTs { token, password } to the accept-invitation Edge Function, which
  // admin-creates a CONFIRMED auth user and provisions the membership. We
  // then sign in with that same password to obtain a browser session.
  const submitCreate = async () => {
    if (password.length < 8) {
      setFormError(`Password must be at least 8 characters. (${8 - password.length} more to go.)`);
      return;
    }
    setAuthBusy(true);
    setFormError(null);
    try {
      const result = await client.functions.invoke('accept-invitation', {
        token,
        password,
      });

      // The invitee already has an account — switch to sign-in instead of
      // resetting their password from an invite link.
      if (result?.status === 'exists') {
        switchMode('signin');
        setFormError('You already have a Pro Haul account with this email. Enter your existing password to accept.');
        return;
      }

      // Account was created but the membership step failed — the sign-in
      // path can still finish the accept, so route them there.
      if (result?.status === 'created_no_membership') {
        switchMode('signin');
        setFormError(result?.error || 'Your account exists — sign in to finish joining the carrier.');
        return;
      }

      if (!result?.ok) {
        setFormError(result?.error || 'Could not accept the invitation. Please try again.');
        return;
      }

      // status === 'created' — account + membership are provisioned.
      // Sign in to get a session, then land in the workspace.
      const { error } = await supabase.auth.signInWithPassword({
        email: preview.invited_email,
        password,
      });
      if (error) throw error;
      await refreshUser();
      toast.success(`Welcome to ${preview.carrier_name}.`);
      navigate('/', { replace: true });
    } catch (e) {
      setFormError(e?.message || 'Something went wrong. Please try again.');
    } finally {
      setAuthBusy(false);
    }
  };

  // ---- sign-in path: existing Pro Haul user accepting the invite ----
  // After a successful sign-in the render flips to the matching-email
  // branch below, which offers the "Accept invitation" button.
  const submitSignIn = async () => {
    if (!password) { setFormError('Enter your password.'); return; }
    setAuthBusy(true);
    setFormError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: preview.invited_email,
        password,
      });
      if (error) throw error;
      // Auth state change re-renders this component → matching-email branch.
    } catch (e) {
      setFormError(e?.message || 'Sign-in failed. Check your password and try again.');
    } finally {
      setAuthBusy(false);
    }
  };

  const submitAuth = (e) => {
    e.preventDefault();
    if (!preview) return;
    if (authMode === 'create') submitCreate();
    else submitSignIn();
  };

  // ============================================================
  // RENDER
  // ============================================================

  if (previewLoading) {
    return (
      <Shell title="Loading invitation…">
        <div className="flex justify-center">
          <div className="w-6 h-6 border-2 border-prohaul-black/10 border-t-prohaul-red rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (previewError) {
    return (
      <Shell title="Invitation unavailable">
        <div className="bg-prohaul-lightgray border border-prohaul-black/10 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-prohaul-red flex-shrink-0 mt-0.5" />
          <p className="text-sm text-prohaul-black">{previewError}</p>
        </div>
        <Button variant="outline" className="w-full bg-white border border-prohaul-black/20 text-prohaul-black hover:bg-prohaul-lightgray" onClick={() => navigate('/')}>
          Back to Prohaul
        </Button>
      </Shell>
    );
  }

  // Non-pending invitation -> state-specific screen
  const statusScreen = renderStatusScreen(preview.status);
  if (statusScreen) {
    return (
      <Shell title={statusScreen.title}>
        <p className="text-sm text-prohaul-black/60 text-center">{statusScreen.body}</p>
        <Button variant="outline" className="w-full bg-white border border-prohaul-black/20 text-prohaul-black hover:bg-prohaul-lightgray" onClick={() => navigate('/')}>
          Back to Prohaul
        </Button>
      </Shell>
    );
  }

  // ---- pending invitation flow ----
  const subtitle = `${preview.carrier_name} invited you ${ROLE_LABEL[preview.invited_role] || `as ${preview.invited_role}`}`;

  // Signed in, but with the wrong account
  if (isAuthenticated && !emailMatches) {
    return (
      <Shell title="Wrong account" subtitle={subtitle}>
        <div className="bg-prohaul-lightgray border border-prohaul-black/10 rounded-xl p-4 space-y-2">
          <p className="text-sm text-prohaul-black">
            This invitation was sent to <strong>{preview.invited_email}</strong>, but you're signed in as <strong>{user?.email}</strong>.
          </p>
          <p className="text-xs text-prohaul-black/60">
            Sign out and sign back in with the invited email to accept.
          </p>
        </div>
        <Button onClick={logout} className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold">
          Sign out
        </Button>
      </Shell>
    );
  }

  // Signed in with matching email — ready to accept
  if (isAuthenticated && emailMatches) {
    return (
      <Shell title="Accept your invitation" subtitle={subtitle}>
        <div className="bg-prohaul-lightgray border border-prohaul-black/10 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm text-prohaul-black">Signed in as <strong>{user?.email}</strong></span>
          </div>
          <p className="text-xs text-prohaul-black/60">
            Accept to join <strong>{preview.carrier_name}</strong> {ROLE_LABEL[preview.invited_role] || preview.invited_role}.
          </p>
        </div>
        <Button onClick={onAccept} disabled={accepting} className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold">
          {accepting ? 'Accepting…' : 'Accept invitation'}
        </Button>
        <button onClick={logout}
          className="w-full text-center text-xs text-prohaul-black/60 hover:text-prohaul-red underline">
          Use a different account
        </button>
      </Shell>
    );
  }

  // ============================================================
  // Not signed in — single-screen accept.
  //   'create' : new user picks a password and joins immediately.
  //   'signin' : invitee who already has a Pro Haul account.
  // ============================================================
  const isCreate = authMode === 'create';
  const tooShort = isCreate && password.length > 0 && password.length < 8;

  return (
    <Shell
      title={isCreate ? 'Accept your invitation' : 'Sign in to accept'}
      subtitle={subtitle}
    >
      <form onSubmit={submitAuth} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-prohaul-black">Email</Label>
          <Input
            id="email"
            type="email"
            value={preview.invited_email}
            disabled
            className="bg-prohaul-lightgray border border-prohaul-black/20 text-prohaul-black/70 opacity-90 cursor-not-allowed"
          />
          <p className="text-xs text-prohaul-black/60">
            Locked to the invited email. To use a different address, ask the carrier admin to re-invite.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-prohaul-black">
            {isCreate ? 'Create a password' : 'Password'}
          </Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); if (formError) setFormError(null); }}
            required
            minLength={isCreate ? 8 : undefined}
            placeholder="••••••••"
            autoComplete={isCreate ? 'new-password' : 'current-password'}
            className={`bg-white text-prohaul-black placeholder:text-prohaul-black/40 border ${tooShort ? 'border-amber-500/60' : 'border-prohaul-black/20'}`}
          />
          {isCreate && (
            <p className={`text-xs ${tooShort ? 'text-amber-600' : 'text-prohaul-black/60'}`}>
              Must be at least 8 characters.{tooShort ? ` (${8 - password.length} more to go.)` : ''}
            </p>
          )}
        </div>

        <ErrorBanner message={formError} />

        <Button type="submit" disabled={authBusy} className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold">
          {authBusy
            ? 'Please wait…'
            : isCreate
              ? `Accept & join ${preview.carrier_name}`
              : 'Sign in & accept'}
        </Button>
      </form>
      <p className="text-center text-xs text-prohaul-black/60">
        {isCreate ? 'Already have a Pro Haul account?' : 'Need to create an account?'}{' '}
        <button
          type="button"
          onClick={() => switchMode(isCreate ? 'signin' : 'create')}
          className="underline hover:text-prohaul-red"
        >
          {isCreate ? 'Sign in instead' : 'Set a password'}
        </button>
      </p>
    </Shell>
  );
}
